/**
 * Dashboard Page — State-Aware Layout Router
 *
 * Reads useIsProvisioned() and useOnboardingProgress() to classify the user
 * into one of three states (setup / onboarding / active), then delegates
 * to the appropriate content component. The shared header (logo + greeting)
 * renders in all states.
 */

// import { Link } from "react-router-dom"; — unused (TS6133)
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ExternalLink } from "lucide-react";

import { usePageTranslation } from "@/i18n/useTranslation";
import { dashboardTranslations } from "@/i18n/locales/en/dashboard";
import { dashboardTranslations as thDashboardTranslations } from "@/i18n/locales/th/dashboard";
import { useCurrentUser } from "@/lib/hooks/useCurrentUser";
import { useAuthStore } from "@/lib/store/authStore";
import { useCustomDomain } from "@/features/storefront/settings/useCustomDomain";
// import { cn } from "@/lib/utils"; — unused (TS6133)
import {
  useRecentActivity,
  useOnboardingProgress,
} from "@/hooks/useDashboardData";
import { useIsProvisioned } from "@/hooks/useIsProvisioned";
import { useOnboardingState } from "@/hooks/useOnboardingState";
import { usePreferencesStore } from "@/lib/store/preferencesStore";
import { DashboardSetupContent } from "./components/DashboardSetupContent";
import { DashboardOnboardingContent } from "./components/DashboardOnboardingContent";
import { DashboardActiveContent } from "./components/DashboardActiveContent";
import { IntentOnboardingPage } from "./components/IntentOnboardingPage";
import { DevModeToggle } from "@/components/view/DevModeToggle";

import { useOnboardingCompleteToast } from "./hooks/useOnboardingCompleteToast";

export function DashboardPage() {
  const t = usePageTranslation({
    en: dashboardTranslations,
    th: thDashboardTranslations,
  });
  const { data: user, isLoading: isLoadingUser } = useCurrentUser();
  const { data: recentActivity = [] } = useRecentActivity();
  const defaultStorefrontUrl = useAuthStore((s) => s.storefrontUrl);
  const { domain: customDomain, status: domainStatus, isLoading: isDomainLoading } = useCustomDomain();
  const storefrontUrl = isDomainLoading ? null
    : domainStatus === "active" && customDomain
      ? `https://${customDomain}`
      : defaultStorefrontUrl;

  // State classification — uses only existing hooks, no new API calls
  const isProvisioned = useIsProvisioned();
  const { data: onboarding, isLoading: isOnboardingLoading, isError: isOnboardingError } = useOnboardingProgress();
  const onboardingState = useOnboardingState();

  // Dev mode: allow overriding dashboard state for testing all views
  const dashboardStateOverride = usePreferencesStore((s) => s.dashboardStateOverride);

  // Intent onboarding (e.g. chat→inbox redirect) should fire even before
  // provisioning completes — the user doesn't need a tenant site for that flow.
  const hasUncompletedIntent = onboardingState.view === "intent-onboarding";
  const isIntentLoading = onboardingState.isLoading;

  const detectedState: "setup" | "onboarding" | "active" =
    hasUncompletedIntent ? "onboarding"
    : !isProvisioned ? "setup"
    : isOnboardingError || !onboarding ? "active"
    : !onboarding.anyPathComplete ? "onboarding"
    : "active";

  const dashboardState = dashboardStateOverride !== 'auto' ? dashboardStateOverride : detectedState;

  // Show loading while intent query resolves (unprovisioned users wait briefly
  // so we don't flash "setup" before the chat redirect fires)
  const isStateLoading = (isProvisioned && isOnboardingLoading)
    || (!isProvisioned && isIntentLoading);
  const reducedMotion = useReducedMotion();
  const duration = reducedMotion ? 0 : 0.35;
  const stateKey = isStateLoading ? "loading" : dashboardState;
  useOnboardingCompleteToast(dashboardState, t.setupComplete);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? t.goodMorning : hour < 17 ? t.goodAfternoon : t.goodEvening;
  const userName = getUserDisplayName(user ?? undefined, isLoadingUser);

  const setupLabels = {
    title: t.setupCardTitle,
    description: t.setupCardDescription,
    contactEmail: t.setupContactEmail,
  };

  const newsLabels = {
    news: t.news,
    newFeature: t.newFeature,
    siteCanvas: t.siteCanvas,
    siteCanvasDescription: t.siteCanvasDescription,
    trySiteCanvas: t.trySiteCanvas,
  };

  const storefrontLabels = {
    yourStorefront: t.yourStorefront,
    openStorefront: t.openStorefront,
    copyUrl: t.copyUrl,
  };

  const intentLabels = {
    question: t.intentPathQuestion,
    paths: {
      selling: { title: t.intentPathSelling, description: t.intentPathSellingDesc },
      chat: { title: t.intentPathChat, description: t.intentPathChatDesc },
      contacts: { title: t.intentPathContacts, description: t.intentPathContactsDesc },
    },
    steps: {
      "add-product": { title: t.intentStepAddProduct, description: t.intentStepAddProductDesc },
      "add-customer": { title: t.intentStepAddCustomer, description: t.intentStepAddCustomerDesc },
      "open-inbox": { title: t.intentStepOpenInbox, description: t.intentStepOpenInboxDesc },
      "add-lead": { title: t.intentStepAddLead, description: t.intentStepAddLeadDesc },
    },
    exploreAll: t.intentPathExploreAll,
    completed: t.intentPathCompleted,
  };

  return (
    <div className="min-h-screen bg-white dark:bg-background md:-mt-4 lg:mt-0 -mb-6">
      <div className="w-full px-4 sm:px-5 lg:px-6 py-3 sm:py-5 lg:py-6">
        {/* ── Header ── */}
        <motion.header
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          className="mb-4 sm:mb-8"
        >
          <div className="hidden md:flex items-center justify-between gap-3 mb-2 sm:mb-5">
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-semibold tracking-tight text-foreground">
                {greeting}{userName && `, ${userName}`}
              </h1>
              {dashboardState === "active" && storefrontUrl ? (
                <a
                  href={storefrontUrl.startsWith("http") ? storefrontUrl : `https://${storefrontUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hidden md:inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors group/link"
                >
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 ring-2 ring-emerald-400/20" />
                  <span>{storefrontUrl}</span>
                  <ExternalLink className="w-3 h-3 opacity-0 group-hover/link:opacity-100 transition-opacity" />
                </a>
              ) : (dashboardState === "setup" || dashboardState === "onboarding") ? (
                <p className="text-xs text-muted-foreground">
                  {dashboardState === "setup" ? t.setupSubtitle : t.onboardingSubtitle}
                </p>
              ) : null}
            </div>
            <DevModeToggle />
          </div>
        </motion.header>

        {/* State-specific content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={stateKey}
            initial={reducedMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration }}
            className="space-y-6"
          >
            {isStateLoading ? (
              <div className="space-y-8">
                <div className="rounded-xl h-40 bg-muted animate-pulse" />
                <div className="rounded-xl h-32 bg-muted animate-pulse" />
              </div>
            ) : dashboardState === "setup" ? (
              <DashboardSetupContent setupLabels={setupLabels} newsLabels={newsLabels} />
            ) : dashboardState === "onboarding" && onboardingState.view === "intent-onboarding" ? (
              <IntentOnboardingPage onboardingState={onboardingState} />
            ) : dashboardState === "onboarding" ? (
              <DashboardOnboardingContent
                paths={onboarding?.paths ?? []}
                intentLabels={intentLabels}
                storefrontUrl={storefrontUrl}
                storefrontLabels={storefrontLabels}
                newsLabels={newsLabels}
                signupIntent={onboardingState.pathId}
              />
            ) : (
              <DashboardActiveContent
                storefrontUrl={storefrontUrl}
                storefrontLabels={storefrontLabels}
                recentActivity={recentActivity}
                recentActivityTitle={t.recentActivity}
                needsAttentionTitle={t.needsAttention}
                newsLabels={newsLabels}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

function getUserDisplayName(
  user: { first_name?: string; last_name?: string; full_name?: string; name?: string } | undefined,
  isLoading: boolean,
): string {
  if (isLoading || !user) return "";
  if (user.first_name && user.last_name) return `${user.first_name} ${user.last_name}`;
  return user.first_name || user.last_name || user.full_name || user.name || "";
}
